<template>
    <v-app>
      <!-- App Bar -->
      <v-app-bar flat class="appbar-gradient" dark>
        <v-icon class="mr-3" color="#d4af37" size="32">mdi-crown-circle</v-icon>
        <v-toolbar-title class="font-weight-bold clublink-title">
          <span style="font-family: 'Playfair Display', serif;">Manage App Progress</span>
        </v-toolbar-title>
        <v-spacer />
        <v-btn text color="#d4af37" @click="goBack" title="Back to Settings">
          <v-icon left>mdi-arrow-left</v-icon> Back
        </v-btn>
      </v-app-bar>
  
      <!-- HERO -->
      <div class="hero">
        <v-container class="py-8">
          <div class="d-flex align-center justify-space-between flex-wrap">
            <div class="white--text mb-4">
              <h1 class="display-1 font-weight-bold mb-1" style="font-family: 'Playfair Display', serif;">Update Customer Journeys</h1>
              <div class="subheading" style="opacity:.9">
                Pick a customer, review their journey, and set the next stage.
              </div>
            </div>
            <v-card class="rounded-xl kpi-card elevation-10">
              <div class="px-4 py-3 d-flex align-center">
                <div class="mr-4 text-center">
                  <div class="overline" style="opacity:.9">Customers</div>
                  <div class="title font-weight-bold">{{ customers.length }}</div>
                </div>
                <v-divider vertical class="mx-2" />
                <div class="text-center">
                  <div class="overline" style="opacity:.9">Completed</div>
                  <div class="title font-weight-bold">{{ completedCount }}</div>
                </div>
              </div>
            </v-card>
          </div>
        </v-container>
        <div class="hero-wave"></div>
      </div>
  
      <!-- Main Content -->
      <v-main>
        <v-container class="mt-n8">
          <v-row justify="center">
            <v-col cols="12" md="9">
              <v-card class="pa-6 rounded-xl elevation-12 clublink-card">
                <h2 class="text-h6 font-weight-bold mb-4 dark-green--text" style="font-family: 'Playfair Display', serif;">
                  <v-icon class="mr-2 gold--text">mdi-account-edit</v-icon>
                  Select Customer and Update Progress
                </h2>
  
                <!-- Customer Picker -->
                <v-autocomplete
                  v-model="selectedUid"
                  :items="customers"
                  :loading="loadingCustomers"
                  item-text="label"
                  item-value="uid"
                  dense
                  clearable
                  outlined
                  hide-selected
                  placeholder="Search by name or email..."
                  class="mb-5 clublink-input"
                  prepend-inner-icon="mdi-magnify"
                  color="#d4af37"
                >
                  <template #item="{ item, on, attrs }">
                    <v-list-item v-on="on" v-bind="attrs">
                      <v-list-item-avatar class="clublink-avatar-small">
                        <span class="gold--text">{{ item.initials }}</span>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title class="font-weight-medium dark-green--text">{{ item.name || '—' }}</v-list-item-title>
                        <v-list-item-subtitle>{{ item.email }}</v-list-item-subtitle>
                      </v-list-item-content>
                      <v-chip x-small :color="chipColor(item.appProgress)" dark>{{ item.appProgress }}</v-chip>
                    </v-list-item>
                  </template>
                </v-autocomplete>
  
                <!-- Nothing selected -->
                <v-alert v-if="!selected" type="info" class="mb-6" border="left" colored-border>
                  <v-icon left>mdi-information</v-icon>
                  Pick a customer above to see their journey.
                </v-alert>
  
                <!-- Summary bar -->
                <v-expand-transition>
                  <v-sheet v-if="selected" class="rounded-xl px-4 py-3 mb-5 summary" elevation="2">
                    <div class="d-flex align-center flex-wrap">
                      <v-avatar size="40" class="mr-3 clublink-avatar-small">
                        <span class="gold--text subtitle-2">{{ selected.initials }}</span>
                      </v-avatar>
                      <div class="mr-4">
                        <div class="subtitle-2 grey--text text--darken-1">Customer</div>
                        <div class="font-weight-bold dark-green--text">{{ selected.name || selected.email }}</div>
                      </div>
                      <v-chip class="mr-2" small :color="chipColor(selected.appProgress)" dark>
                        <v-icon left small>mdi-flag-checkered</v-icon>{{ selected.appProgress }}
                      </v-chip>
                      <v-chip small class="mr-2 gold-chip" v-if="nextStep">
                        <v-icon left small>mdi-compass-outline</v-icon>
                        Next: {{ nextStep }}
                      </v-chip>
                      <v-spacer/>
                      <div class="caption grey--text">
                        Last updated: {{ lastStageDate || '—' }}
                      </div>
                    </div>
                  </v-sheet>
                </v-expand-transition>
  
                <!-- Timeline Journey -->
                <transition name="fade">
                  <div v-if="selected" class="mb-6">
                    <h3 class="text-subtitle-1 font-weight-bold mb-3 dark-green--text">
                      <v-icon class="mr-2 gold--text">mdi-timeline-clock</v-icon>
                      App Progress Journey
                    </h3>
                    <v-timeline align="start" dense>
                      <v-timeline-item
                        v-for="step in progressOptions"
                        :key="step"
                        :color="isStepComplete(step) ? '#4caf50' : 'grey lighten-1'"
                        :icon="isStepComplete(step) ? 'mdi-check-decagram' : 'mdi-progress-clock'"
                        fill-dot
                      >
                        <v-card :class="{'glow-card': isStepComplete(step)}" class="pa-3 rounded-lg clublink-timeline-card" elevation="4">
                          <div class="d-flex justify-space-between align-center">
                            <div class="font-weight-bold dark-green--text">{{ step }}</div>
                            <div class="caption grey--text">
                              {{ stageDate(step) || (isStepComplete(step) ? '—' : '') }}
                            </div>
                          </div>
                        </v-card>
                      </v-timeline-item>
                    </v-timeline>
                  </div>
                </transition>
  
                <!-- Quick set + selector -->
                <v-expand-transition>
                  <div v-if="selected">
                    <div class="mb-3 d-flex align-center flex-wrap">
                      <span class="subtitle-2 mr-2 dark-green--text font-weight-medium">Quick set:</span>
                      <v-chip
                        v-for="s in progressOptions"
                        :key="s"
                        class="mr-2 mb-2 quick-chip"
                        :class="{'active-chip': newProgress===s}"
                        small
                        @click="newProgress = s"
                      >
                        {{ s }}
                      </v-chip>
                    </div>
  
                    <v-select
                      v-model="newProgress"
                      :items="progressOptions"
                      label="Or choose a new stage"
                      outlined
                      dense
                      class="mb-4 clublink-input"
                      color="#d4af37"
                    />
                  </div>
                </v-expand-transition>
  
                <!-- Update Button -->
                <v-btn
                  color="#d4af37"
                  dark
                  block
                  large
                  class="clublink-btn"
                  :disabled="!selected || !newProgress || newProgress===selected?.appProgress"
                  @click="updateProgress"
                  :loading="updating"
                >
                  <v-icon left>mdi-sync</v-icon>
                  Update App Progress
                </v-btn>
  
                <!-- Message Alert -->
                <v-alert
                  v-if="message"
                  :type="messageType"
                  class="mt-4"
                  border="left"
                  colored-border
                  elevation="2"
                  dismissible
                  @input="message=''"
                >
                  {{ message }}
                </v-alert>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-main>
  
      <!-- Snackbar -->
      <v-snackbar v-model="snack.show" :timeout="2400" bottom right color="#2d4a3e">
        {{ snack.msg }}
        <v-btn text color="#d4af37" @click="snack.show=false">Close</v-btn>
      </v-snackbar>
  
      <!-- Footer -->
      <v-footer class="footer-gradient" dark padless>
        <v-col class="text-center py-4">
          <span style="font-family: 'Montserrat', sans-serif;">
            © {{ new Date().getFullYear() }} ClubLink Software. All rights reserved.
          </span>
        </v-col>
      </v-footer>
    </v-app>
  </template>
  
  <script setup>
  import { ref, computed, onMounted } from 'vue'
  import { db } from '../firebase'
  import {
    collection, getDocs, updateDoc, doc, query, where, serverTimestamp, getDoc
  } from 'firebase/firestore'
  import router from '../router'
  
  /* -------- State -------- */
  const customers = ref([])              // [{ uid, email, name, initials, appProgress, appProgressDates, label }]
  const loadingCustomers = ref(true)
  const selectedUid = ref(null)          // stores the uid of the pick
  const newProgress = ref('')
  const updating = ref(false)
  const message = ref('')
  const messageType = ref('success')
  const snack = ref({ show:false, msg:'' })
  
  /* -------- Constants -------- */
  const progressOptions = ['Not Started','Designing','Developing','Testing','Completed']
  
  /* -------- Derived -------- */
  const selected = computed(() => customers.value.find(c => c.uid === selectedUid.value) || null)
  const progressIndex = computed(() => selected.value ? progressOptions.indexOf(selected.value.appProgress) : -1)
  const nextStep = computed(() => {
    if (progressIndex.value < 0) return ''
    const nxt = progressOptions[progressIndex.value + 1]
    return nxt || ''
  })
  const completedCount = computed(() => customers.value.filter(c => c.appProgress === 'Completed').length)
  const lastStageDate = computed(() => {
    if (!selected.value) return ''
    const key = selected.value.appProgress
    return stageDate(key)
  })
  
  /* -------- Helpers -------- */
  function initialsFrom (name='', email='') {
    const nm = (name || '').trim()
    if (nm) return nm.split(/\s+/).map(p=>p[0]).join('').slice(0,2).toUpperCase()
    return (email.split('@')[0] || '??').slice(0,2).toUpperCase()
  }
  function chipColor(stage) {
    switch ((stage||'').toLowerCase()) {
      case 'not started': return 'grey darken-1'
      case 'designing':   return '#2196f3'
      case 'developing':  return '#9c27b0'
      case 'testing':     return '#ff9800'
      case 'completed':   return '#4caf50'
      default:            return 'grey'
    }
  }
  function prettyDate(ts){
    const d = ts?.toDate ? ts.toDate() : (ts?.seconds ? new Date(ts.seconds*1000) : (ts instanceof Date ? ts : null))
    return d ? d.toLocaleDateString(undefined,{month:'short',day:'numeric',year:'numeric'}) : ''
  }
  function stageDate(step){
    if (!selected.value?.appProgressDates) return ''
    return prettyDate(selected.value.appProgressDates[step])
  }
  function isStepComplete(step){
    if (!selected.value) return false
    return progressOptions.indexOf(step) <= progressOptions.indexOf(selected.value.appProgress || 'Not Started')
  }
  
  /* -------- Data -------- */
  async function fetchCustomers(){
    loadingCustomers.value = true
    try {
      const q = query(collection(db, 'users'), where('role','==','Customer'))
      const snap = await getDocs(q)
      customers.value = snap.docs.map(d => {
        const data = d.data() || {}
        const name = [data.firstName, data.lastName].filter(Boolean).join(' ') || (data.fullName || '')
        return {
          uid: d.id,
          email: data.email || '',
          name,
          initials: initialsFrom(name, data.email),
          appProgress: data.appProgress || 'Not Started',
          appProgressDates: data.appProgressDates || {},
          label: `${name || '(No name)'} — ${data.email || ''}`
        }
      })
    } catch (e) {
      console.error('fetchCustomers', e)
      message.value = '❌ Failed to load customers.'
      messageType.value = 'error'
    } finally {
      loadingCustomers.value = false
    }
  }
  
  /* -------- Actions -------- */
  async function updateProgress(){
    if (!selected.value || !newProgress.value) return
    updating.value = true
    try {
      // 1) Update users doc (primary source used by the app)
      await updateDoc(doc(db, 'users', selected.value.uid), {
        appProgress: newProgress.value,
        [`appProgressDates.${newProgress.value}`]: serverTimestamp()
      })
  
      // 2) If you also mirror into 'customers', update it silently (ignore if missing)
      try {
        const cRef = doc(db, 'customers', selected.value.uid)
        const cSnap = await getDoc(cRef)
        if (cSnap.exists()) {
          await updateDoc(cRef, {
            appProgress: newProgress.value,
            [`appProgressDates.${newProgress.value}`]: serverTimestamp()
          })
        }
      } catch (_) {}
  
      snack.value = { show:true, msg:'Progress updated.' }
      message.value = '✅ App progress updated successfully!'
      messageType.value = 'success'
  
      // Refresh local view
      await fetchCustomers()
      selectedUid.value = selected.value.uid
      newProgress.value = ''
    } catch (e) {
      console.error('updateProgress', e)
      message.value = '❌ Failed to update app progress.'
      messageType.value = 'error'
    } finally {
      updating.value = false
    }
  }
  
  function goBack(){ router.push('/admin-settings') }
  
  /* -------- Init -------- */
  onMounted(fetchCustomers)
  </script>
  
  <style scoped>
  /* ClubLink Colors */
  .gold--text { color: #d4af37 !important; }
  .dark-green--text { color: #2d4a3e !important; }
  
  /* App bar + footer */
  .appbar-gradient{ 
    background: linear-gradient(135deg, #1a2e26 0%, #2d4a3e 50%, #1a2e26 100%);
  }
  .footer-gradient{ 
    background: linear-gradient(135deg, #1a2e26 0%, #2d4a3e 100%);
  }
  
  .clublink-title {
    font-size: 20px !important;
  }
  
  /* Hero */
  .hero{
    position:relative;
    background: linear-gradient(135deg, #1a2e26 0%, #2d4a3e 50%, #1a2e26 100%);
    color:#fff;
    padding-bottom: 20px;
  }
  
  .hero::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(212, 175, 55, 0.08) 0%, transparent 50%);
    pointer-events: none;
  }
  
  .hero-wave{
    height:20px;
    background: radial-gradient(100% 22px at 50% -8px, rgba(255,255,255,.92) 60%, rgba(255,255,255,0) 61%) repeat-x;
    background-size:30px 22px;
  }
  
  .kpi-card{ 
    background: linear-gradient(135deg, rgba(212, 175, 55, 0.15), rgba(201, 169, 97, 0.12));
    color:#fff; 
    backdrop-filter: saturate(140%) blur(4px); 
    border: 2px solid rgba(212, 175, 55, 0.3);
  }
  
  /* Cards */
  .clublink-card {
    border-top: 4px solid #d4af37 !important;
    background: white;
    position: relative;
    overflow: hidden;
  }
  
  .clublink-card::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 200px;
    height: 100%;
    background: linear-gradient(135deg, transparent, rgba(212, 175, 55, 0.03));
    pointer-events: none;
  }
  
  .rounded-xl{ border-radius:16px; }
  
  .summary{ 
    background: linear-gradient(135deg, rgba(212, 175, 55, 0.05), rgba(201, 169, 97, 0.03));
    border: 2px solid rgba(212, 175, 55, 0.2);
  }
  
  /* Avatars */
  .clublink-avatar-small {
    background: linear-gradient(135deg, #2d4a3e, #1a2e26) !important;
  }
  
  /* Timeline */
  .clublink-timeline-card {
    background: rgba(212, 175, 55, 0.03) !important;
    transition: all 0.3s ease;
    border-left: 3px solid transparent;
  }
  
  .clublink-timeline-card:hover {
    transform: translateX(4px);
    box-shadow: 0 4px 12px rgba(212, 175, 55, 0.2) !important;
  }
  
  .glow-card{ 
    box-shadow: 0 0 14px 2px rgba(212, 175, 55, 0.45) !important;
    transition: box-shadow .3s ease;
    background: linear-gradient(135deg, rgba(76, 175, 80, 0.05), rgba(76, 175, 80, 0.02)) !important;
    border-left: 3px solid #4caf50 !important;
  }
  
  /* Quick set chips */
  .quick-chip {
    background: rgba(212, 175, 55, 0.1) !important;
    color: #2d4a3e !important;
    border: 2px solid rgba(212, 175, 55, 0.2);
    transition: all 0.2s ease;
    cursor: pointer;
  }
  
  .quick-chip:hover {
    background: rgba(212, 175, 55, 0.2) !important;
    border-color: rgba(212, 175, 55, 0.4);
    transform: translateY(-2px);
  }
  
  .quick-chip.active-chip {
    background: linear-gradient(135deg, #d4af37, #c9a961) !important;
    color: #2d4a3e !important;
    border-color: #d4af37;
    font-weight: 600;
    box-shadow: 0 4px 12px rgba(212, 175, 55, 0.3);
  }
  
  .gold-chip {
    background: linear-gradient(135deg, rgba(212, 175, 55, 0.15), rgba(201, 169, 97, 0.1)) !important;
    color: #2d4a3e !important;
    border: 1px solid rgba(212, 175, 55, 0.3);
  }
  
  /* Form Inputs */
  .clublink-input >>> .v-input__slot {
    background-color: #f8f6f1 !important;
    border-radius: 8px !important;
  }
  
  .clublink-input >>> fieldset {
    border-color: #e0e0e0 !important;
    border-width: 2px !important;
  }
  
  .clublink-input >>> .v-input--is-focused fieldset {
    border-color: #d4af37 !important;
  }
  
  /* Buttons */
  .clublink-btn {
    font-family: 'Montserrat', sans-serif !important;
    font-weight: 600 !important;
    letter-spacing: 0.5px !important;
  }
  
  .clublink-btn:not(:disabled):hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(212, 175, 55, 0.4) !important;
  }
  
  /* Smooth fade transition */
  .fade-enter-active, .fade-leave-active{ transition: opacity .35s ease; }
  .fade-enter-from, .fade-leave-to{ opacity:0; }
  </style>